/**
 * Design system component: ItemGrid
 *
 * Responsive container that renders items in the correct layout for the
 * current breakpoint:
 *   - Mobile  → stacked `ItemListRow` list with dividers between rows
 *   - Tablet  → 2-column `ItemCard` photo grid
 *   - Desktop → 3-column `ItemCard` photo grid
 *
 * Uses `useBreakpoint()` to detect the breakpoint. All items receive
 * `currentUserId` so each child can personalise status indicators (e.g.
 * "with you" vs "on loan") without a separate prop-drilling pass.
 */
import { useBreakpoint } from '@/hooks/useMobile';
import { ItemListRow } from './ItemListRow';
import { ItemCard } from './ItemCard';
import { SPACE } from '@/lib/tokens';
import type { ItemWithOwner } from '@/hooks/useItems';

interface ItemGridProps {
  items: ItemWithOwner[];
  currentUserId: string;
  onItemClick: (itemId: string) => void;
  groupId?: string;
}

/**
 *
 * @param root0
 * @param root0.items
 * @param root0.currentUserId
 * @param root0.onItemClick
 * @param root0.groupId
 */
export function ItemGrid({ items, currentUserId, onItemClick, groupId }: ItemGridProps) {
  const bp = useBreakpoint();
  const isGrid = bp === 'tablet' || bp === 'desktop';
  const columns = bp === 'desktop' ? 3 : 2;

  if (!isGrid) {
    return (
      <div>
        {items.map((item, idx) => (
          <ItemListRow
            key={item.id}
            item={item}
            currentUserId={currentUserId}
            onClick={() => onItemClick(item.id)}
            isLast={idx === items.length - 1}
            groupId={groupId}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: SPACE.cardPad,
      }}
    >
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          currentUserId={currentUserId}
          onClick={() => onItemClick(item.id)}
          groupId={groupId}
        />
      ))}
    </div>
  );
}
